import {
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField
} from '@mui/material';
import { useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

/**
 * ChatBotRequestDetails component
 *
 * Renders a form to configure chatbot request details, including conversation ID,
 * prompt code, RAG settings, and parameters.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.request - The request details.
 * @param {Function} props.onSubmit - Callback function when the form is submitted.
 * @returns {JSX.Element} The rendered component.
 */
export default function ChatBotRequestDetails({ request, onSubmit }) {
  const { formatMessage: msg } = useIntl();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      conversationId: '',
      promptCode: '',
      ragActivated: false,
      ragFilter: '',
      ragRelationFilter: '',
      ragRelationType: '',
      parameters: ''
    }
  });

  function buildRequestForm(request) {
    const { conversationId, promptCode, parameters, ragRequest } = request;

    const requestForm = {
      conversationId: conversationId || '',
      promptCode: promptCode || '',
      parameters: parameters ? JSON.stringify(parameters) : '',
      ragActivated: false,
      ragFilter: '',
      ragRelationFilter: '',
      ragRelationType: ''
    };

    if (ragRequest) {
      const { activated, filter, relationFilter } = ragRequest;
      requestForm.ragActivated = activated || false;
      requestForm.ragFilter = filter || '';
      if (relationFilter) {
        requestForm.ragRelationFilter = relationFilter.filter || '';
        requestForm.ragRelationType = relationFilter.relationType || '';
      }
    }

    return requestForm;
  }

  const resetQuery = useCallback(() => {
    reset({
      ...buildRequestForm(request)
    });
  }, [request, reset]);

  const handleReset = () => {
    resetQuery();
  };

  const clearField = (fieldName) => {
    setValue(fieldName, '');
  };

  useEffect(() => {
    resetQuery();
  }, [request, resetQuery]);

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      spacing={2}
      display="flex"
      direction="column"
      sx={{ height: '100%' }}
    >
      <MDTypography variant="h5">Configuration de la requête</MDTypography>
      <Controller
        name="conversationId"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={msg({ id: 'page.chatbot.conversationId', defaultMessage: 'Conversation ID' })}
            variant="filled"
            error={!!errors.conversationId}
            helperText={errors.conversationId ? errors.conversationId.message : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={msg({ id: 'tooltip.clear' })}>
                    <IconButton onClick={() => clearField('conversationId')}>
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
        )}
      />

      <Controller
        name="promptCode"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={msg({ id: 'page.chatbot.promptCode', defaultMessage: 'Prompt Code' })}
            variant="filled"
            error={!!errors.promptCode}
            helperText={errors.promptCode ? errors.promptCode.message : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={msg({ id: 'tooltip.clear' })}>
                    <IconButton onClick={() => clearField('promptCode')}>
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
        )}
      />

      <Controller
        name="ragActivated"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Checkbox {...field} checked={field.value} />}
            label={msg({ id: 'page.chatbot.ragActivated', defaultMessage: 'RAG Activated' })}
          />
        )}
      />

      <Controller
        name="ragFilter"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={msg({ id: 'page.chatbot.ragFilter', defaultMessage: 'RAG Filter' })}
            variant="filled"
            error={!!errors.ragFilter}
            helperText={errors.ragFilter ? errors.ragFilter.message : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={msg({ id: 'tooltip.clear' })}>
                    <IconButton onClick={() => clearField('ragFilter')}>
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
        )}
      />

      <Controller
        name="ragRelationFilter"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={msg({
              id: 'page.chatbot.ragRelationFilter',
              defaultMessage: 'RAG Relation Filter'
            })}
            variant="filled"
            error={!!errors.ragRelationFilter}
            helperText={errors.ragRelationFilter ? errors.ragRelationFilter.message : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={msg({ id: 'tooltip.clear' })}>
                    <IconButton onClick={() => clearField('ragRelationFilter')}>
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
        )}
      />

      <Controller
        name="ragRelationType"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={msg({ id: 'page.chatbot.ragRelationType', defaultMessage: 'RAG Relation Type' })}
            variant="filled"
            error={!!errors.ragRelationType}
            helperText={errors.ragRelationType ? errors.ragRelationType.message : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={msg({ id: 'tooltip.clear' })}>
                    <IconButton onClick={() => clearField('ragRelationType')}>
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
        )}
      />

      <Controller
        name="parameters"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={msg({ id: 'page.chatbot.parameters', defaultMessage: 'Parameters (JSON)' })}
            variant="filled"
            error={!!errors.parameters}
            helperText={errors.parameters ? errors.parameters.message : ''}
            multiline
            minRows={3}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={msg({ id: 'tooltip.clear' })}>
                    <IconButton onClick={() => clearField('parameters')}>
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
        )}
      />

      <Stack direction="row" spacing={2} sx={{ marginTop: 'auto !important' }}>
        <MDButton type="button" fullWidth color="secondary" onClick={handleReset}>
          {msg({ id: 'button.reset' })}
        </MDButton>
        <MDButton type="submit" fullWidth color="primary">
          {msg({ id: 'button.validate' })}
        </MDButton>
      </Stack>
    </Stack>
  );
}

ChatBotRequestDetails.propTypes = {
  request: PropTypes.shape({
    conversationId: PropTypes.string,
    promptCode: PropTypes.string,
    ragRequest: PropTypes.shape({
      activated: PropTypes.bool,
      filter: PropTypes.string,
      relationFilter: PropTypes.shape({
        filter: PropTypes.string,
        relationType: PropTypes.string
      })
    }),
    parameters: PropTypes.object
  }).isRequired,
  onSubmit: PropTypes.func.isRequired
};
